import { styled } from "styled-components";
import { ArrowRight, Check, Mail, ShieldCheck } from "lucide-react";
import { Hero } from "../components/Hero";
import { ArrowText, ButtonLink, ButtonRow, Card, Container, Grid, Pill, Section, SectionHeader, TextLink } from "../components/UI";
import { SEO, organizationSchema } from "../seo";
import { packages, processSteps, services, site, valuePoints } from "../data";

const Band = styled.div`
  border-block:1px solid ${({theme})=>theme.colors.border};
  background:${({theme})=>theme.colors.canvasAlt};
`;
const ServiceCard = styled(Card)`
  min-height:310px;
  display:flex;
  flex-direction:column;
  transition:transform .25s ease,border-color .25s ease;
  &:hover{transform:translateY(-6px);border-color:${({theme})=>theme.colors.primary};}
  h3{font-size:1.45rem;line-height:1.12;margin:1.1rem 0 .7rem;}
  p{color:${({theme})=>theme.colors.muted};margin:0 0 1.25rem;}
  a{margin-top:auto;}
`;
const IconBox = styled.div`
  width:50px;height:50px;border-radius:16px;display:grid;place-items:center;color:${({theme})=>theme.colors.primary};
  background:${({theme})=>theme.colors.surfaceElevated};border:1px solid ${({theme})=>theme.colors.border};
`;
const Promise = styled.div`
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:2rem;
  align-items:center;
  @media(max-width:780px){grid-template-columns:1fr;}
`;
const CompassPanel = styled(Card)`
  position: relative;
  min-height: 430px;
  display: grid;
  place-items: center;
  overflow: hidden;
  padding: clamp(1.5rem, 4vw, 3rem);

  img {
    position: relative;
    z-index: 2;
    display: block;
    width: min(88%, 410px);
    max-width: 100%;
    height: auto;
    aspect-ratio: 1 / 1;
    object-fit: contain;
    filter: drop-shadow(0 18px 34px rgba(0, 0, 0, 0.2));
  }

  &::before {
    content: "";
    position: absolute;
    width: 330px;
    height: 330px;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      rgba(69, 183, 255, 0.28),
      transparent 68%
    );
    top: -90px;
    right: -80px;
  }

  &::after {
    content: "";
    position: absolute;
    width: 300px;
    height: 300px;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      rgba(21, 200, 117, 0.22),
      transparent 68%
    );
    bottom: -110px;
    left: -70px;
  }
`;
const Step = styled(Card)`
  span{display:block;font-size:2.7rem;line-height:1;font-weight:900;color:${({theme})=>theme.colors.border};}
  h3{margin:.8rem 0 .4rem;}
  p{margin:0;color:${({theme})=>theme.colors.muted};}
`;
const Package = styled(Card)`
  display:flex;flex-direction:column;
  h3{font-size:1.55rem;margin:.9rem 0 .35rem;}
  p{color:${({theme})=>theme.colors.muted};}
  ul{padding-left:1.1rem;color:${({theme})=>theme.colors.muted};}
  a{margin-top:auto;}
`;
const CheckRow = styled.div`
  display:flex;align-items:flex-start;gap:.7rem;padding:.9rem 0;border-top:1px solid ${({theme})=>theme.colors.border};
  svg{color:${({theme})=>theme.colors.accent};flex:0 0 auto;margin-top:.18rem;}
  strong{display:block;} small{color:${({theme})=>theme.colors.muted};}
`;

export function Home() {
  return <>
    <SEO title="TPeck Digital | Websites, Hosting, SEO, Marketing & Business Automation" description={site.promise} schema={organizationSchema}/>
    <Hero/>

    <Band><Section $compact><Container>
      <Grid $min="220px">
        {valuePoints.map(([Icon,title,text])=><div key={title} style={{display:"flex",gap:".8rem",alignItems:"flex-start"}}><Icon size={25} style={{flex:"0 0 auto"}}/><div><strong>{title}</strong><div style={{opacity:.72}}>{text}</div></div></div>)}
      </Grid>
    </Container></Section></Band>

    <Section><Container>
      <SectionHeader eyebrow="Complete digital support" title="Everything your growing business needs to look professional and work smarter." lead="TPeck Digital builds on its original web design, SEO, and marketing foundation with managed hosting, business email, practical automation, and custom digital tools." action={<ButtonLink to="/services" $secondary>View all services</ButtonLink>}/>
      <Grid $min="270px">{services.map(service=>{const Icon=service.icon;return <ServiceCard key={service.slug}><IconBox><Icon/></IconBox><Pill style={{width:"fit-content",marginTop:"1rem"}}>{service.eyebrow}</Pill><h3>{service.name}</h3><p>{service.summary}</p><TextLink to={`/${service.slug}`}><ArrowText>Explore service</ArrowText></TextLink></ServiceCard>})}</Grid>
    </Container></Section>

    <Band><Section><Container>
      <Promise>
        <div>
          <SectionHeader eyebrow="Our original promise, expanded" title="Take the stress out of your digital presence." lead="Growing a business is difficult enough without trying to coordinate a website developer, hosting company, email provider, SEO specialist, advertising manager, and automation consultant. TPeck Digital brings those needs together through one practical relationship."/>
          <ButtonRow><ButtonLink to="/about">Learn about TPeck Digital</ButtonLink><ButtonLink to="/mission" $secondary>Mission & vision</ButtonLink></ButtonRow>
        </div>
        <CompassPanel>
  <img
    src="/tpeck-logo-transparent.png"
    alt="TPeck Digital compass logo"
  />
</CompassPanel>
      </Promise>
    </Container></Section></Band>

    <Section><Container>
      <SectionHeader eyebrow="Website, hosting, and email" title="A dependable digital foundation—not another collection of vendor accounts." lead="Every managed hosting package includes business email, along with the essential technical support needed to keep the website available, protected, and current."/>
      <Grid $min="320px">
        <Card><Mail size={30}/><h3>Business email included</h3><p style={{opacity:.72}}>Use a professional address at the business domain. Mailbox quantities and storage are defined in the selected hosting plan.</p></Card>
        <Card><ShieldCheck size={30}/><h3>Managed and supported</h3><p style={{opacity:.72}}>SSL, backups, updates, monitoring, domain configuration, and troubleshooting are coordinated through TPeck Digital.</p></Card>
        <Card><ArrowRight size={30}/><h3>Ready to grow</h3><p style={{opacity:.72}}>Add SEO, campaigns, landing pages, automation, and business tools without starting over with a new digital provider.</p></Card>
      </Grid>
      <ButtonRow><ButtonLink to="/hosting-email">Explore hosting & email</ButtonLink></ButtonRow>
    </Container></Section>

    <Band><Section><Container>
      <SectionHeader eyebrow="How we work" title="A straightforward path from need to solution." lead="The goal is to make technology easier for the business—not to make the engagement feel more technical than it needs to be."/>
      <Grid $min="205px">{processSteps.map(([number,title,text])=><Step key={number}><span>{number}</span><h3>{title}</h3><p>{text}</p></Step>)}</Grid>
    </Container></Section></Band>

    <Section><Container>
      <SectionHeader eyebrow="Service packages" title="Start with the level of support your business needs today." lead="Packages provide a clear starting structure. Final scope and pricing are based on the website, content, email, marketing, integration, and support requirements." action={<ButtonLink to="/pricing" $secondary>Compare packages</ButtonLink>}/>
      <Grid $min="260px">{packages.map(item=><Package key={item.name}><Pill>Tailored quote</Pill><h3>{item.name}</h3><p>{item.audience}</p><ul>{item.includes.slice(0,5).map(x=><li key={x}>{x}</li>)}</ul><TextLink to="/pricing"><ArrowText>View package</ArrowText></TextLink></Package>)}</Grid>
    </Container></Section>

    <Band><Section><Container>
      <Grid $min="340px" $gap="2rem">
        <div><SectionHeader eyebrow="AI and automation" title="Use modern technology where it makes business sense." lead="Custom AI agents and workflow automation are offered as practical extensions of the business—not as technology for its own sake."/><ButtonLink to="/ai-automation">Explore AI & automation</ButtonLink></div>
        <Card>
          <h3 style={{fontSize:"1.55rem",marginTop:0}}>Good starting points</h3>
          <CheckRow><Check size={19}/><div><strong>Answer common customer questions</strong><small>Provide useful responses and route more complex needs to a person.</small></div></CheckRow>
          <CheckRow><Check size={19}/><div><strong>Improve lead follow-up</strong><small>Connect forms, notifications, qualification, and reminders.</small></div></CheckRow>
          <CheckRow><Check size={19}/><div><strong>Organize internal information</strong><small>Help staff find approved business information faster.</small></div></CheckRow>
          <CheckRow><Check size={19}/><div><strong>Reduce repetitive administration</strong><small>Automate routine movement of data, documents, and approvals.</small></div></CheckRow>
        </Card>
      </Grid>
    </Container></Section></Band>

    <Section><Container>
      <Card>
        <SectionHeader eyebrow="Start with a conversation" title="Bring the business challenge—not a predetermined technology solution." lead="TPeck Digital will help clarify the need, identify realistic options, and recommend a useful next step."/>
        <ButtonRow><ButtonLink to="/contact#schedule">{site.consultationLabel} <ArrowRight size={18}/></ButtonLink><ButtonLink to="/contact" $secondary>Send a message</ButtonLink></ButtonRow>
      </Card>
    </Container></Section>
  </>;
}
